import styled from '@emotion/styled';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import type { MenuProps } from 'antd';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useStore } from '../../store/reducers';
import { IconCaretDown } from '../BasicComponents/Icons';
import { useAdminMenuItems } from './useAdminMenuItems';
import { useTrackersMenuItems } from './useTrackersMenuItems';

export const useMainNavItems = (
  showCarets = false,
): { items: NonNullable<MenuProps['items']>; impersonateUserModal: ReactNode } => {
  const currentUser = useStore((s) => s.users.getter.currentUser());
  const isImpersonating = currentUser?.impersonated === true;
  const { stopImpersonation } = useStore((s) => s.users.actions);
  const trackersItems = useTrackersMenuItems();
  const { items: adminItems, impersonateUserModal } = useAdminMenuItems();

  const items = [
    {
      key: '/admin/events',
      label: (
        <Link to="/admin/events" id="nav_link_events">
          Tracking Maps
        </Link>
      ),
    },
    trackersItems.length > 0
      ? {
          key: 'trackers',
          className: TestSelectors.admin.nav.dropdowns.trackers.toString(),
          label: showCarets ? (
            <LabelWithCaret id={TestSelectors.admin.nav.dropdowns.trackers.toString()}>
              Trackers
              <IconCaretDown />
            </LabelWithCaret>
          ) : (
            <span id={TestSelectors.admin.nav.dropdowns.trackers.toString()}>Trackers</span>
          ),
          children: trackersItems,
        }
      : null,
    adminItems.length > 0
      ? {
          key: 'admin',
          className: TestSelectors.admin.nav.dropdowns.admin.toString(),
          label: showCarets ? (
            <LabelWithCaret id={TestSelectors.admin.nav.dropdowns.admin.toString()}>
              Admin
              <IconCaretDown />
            </LabelWithCaret>
          ) : (
            <span id={TestSelectors.admin.nav.dropdowns.admin.toString()}>Admin</span>
          ),
          children: adminItems,
        }
      : null,
    {
      key: 'support',
      label: (
        <a href="https://docs.racemap.com/" id="nav_link_support" target="_blank" rel="noreferrer">
          Support
        </a>
      ),
    },
    // While impersonating, useIsAdmin() is false and the Admin dropdown is gone —
    // this is the only way back.
    isImpersonating
      ? {
          key: 'back-admin',
          label: `Back to Admin (impersonating ${currentUser?.name || currentUser?.email})`,
          onClick: stopImpersonation,
        }
      : null,
  ].filter((item) => item != null);

  return { items, impersonateUserModal };
};

const LabelWithCaret = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
